import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';

import loadable from '@loadable/component';

import { Water, DropletFill, ClockHistory, ArrowRightCircleFill } from 'react-bootstrap-icons';
import Badge from 'react-bootstrap/Badge';

import CommonHeaders from '../helmet-headers/CommonHeaders.js';

import {StationHistoricalFlowLinkCalculator} from '../../lib/LinkCalculators.js';

import { formatTimestamp } from '../../lib/formatHelpers.js';

const WaterLevelGauge = loadable(() => import('../WaterLevelGauge.js'));
const WaterLevelLineChart = loadable(() => import('../WaterLevelLineChart.js'));
const FloodEventHistoryButton = loadable(() => import('../FloodEventHistoryButton.js'));
const CentralLink = loadable(() => import('../CentralLink.js'));

const RenderStationFlowBased = function({ isMissingMetrics, station, qualifierType, computedQualifierType }) {

	let stationName = station && station.stationName;

	let waterLevelGaugeRender = null;
	let typicalFlowLowSafe = null;
	let typicalFlowHighSafe = null;
	let maxFlowOnRecordSafe = null;
	if(station && station.metadata) {
		waterLevelGaugeRender = <WaterLevelGauge
		  maxOnRecord={null}
		  minOnRecord={null}
		  typicalRangeHigh={station.metadata.typicalFlowHigh}
		  typicalRangeLow={station.metadata.typicalFlowLow}
		  currentValue={station.currentFlow} // Current value for demonstration
		  gaugeLabel={stationName}
		  forecastValue={null}
		/>

		typicalFlowLowSafe = (Math.floor(parseFloat(station.metadata.typicalFlowLow)*100)/100);
		typicalFlowHighSafe = (Math.floor(parseFloat(station.metadata.typicalFlowHigh)*100)/100);
		maxFlowOnRecordSafe = (Math.floor(parseFloat(station.metadata.maxFlowOnRecord)*100)/100);
		if(isNaN(typicalFlowLowSafe)) {
			typicalFlowLowSafe = null;
		} else {
			typicalFlowLowSafe = typicalFlowLowSafe.toLocaleString();
		}
		if(isNaN(typicalFlowHighSafe)) {
			typicalFlowHighSafe = null;
		} else {
			typicalFlowHighSafe = typicalFlowHighSafe.toLocaleString();
		}
		if(isNaN(maxFlowOnRecordSafe)) {
			maxFlowOnRecordSafe = null;
		} else {
			maxFlowOnRecordSafe = maxFlowOnRecordSafe.toLocaleString();
		}
	}

	let metaTitle = "The " + (station.riverName ? station.riverName : "river") + " flow at the " + stationName + " station";
	if(station.placeName) {
		metaTitle += " in " + station.placeName;
	}
	metaTitle += ".";

	let metaDescription = "Check the latest " + (station.riverName ? station.riverName + " " : "") + "flow data at the " + stationName + " measuring station" + (station.placeName ? " in " + station.placeName : "") + ". View local data and stay informed with real-time updates and alerts.";

	return [<CommonHeaders
			key={"helmet_flow_h_"+station.metricId}
			title={metaTitle}
			description={metaDescription}
			/*Canonical handled by station handler*/
			/*townName handled by station handler*/
			/*ogType handled by station handler*/
			/*lat/lng handled by station handler*/
		/>, <Row key={"flow_topview"}>
		<Col className="d-none d-md-block" md={1} lg={2} />
		<Col xs={4} md={3} lg={2}>
			<div style={{height: waterLevelGaugeRender ? "280px" : "100%", display: "flex", justifyContent: "center", alignItems: "center"}}>
				{waterLevelGaugeRender || <div><b><DropletFill /></b><br /><i>No gauge for this station</i></div>}
			</div>
		</Col>
		<Col xs={8} md={7} lg={6}>
			{station && station.currentFlow ? <Row>
				<Col xs={12}><center><strong className="hX-scale20em"><Badge pill bg="dark"><ArrowRightCircleFill /> {station.currentFlow} m3/sec</Badge></strong></center></Col>
				<Col xs={12}>Is the flow level here, for the {station.riverName ? <b>{station.riverName}</b> : 'station'}, recorded at <b>{formatTimestamp(station.currentFlowTimestamp)}</b>.</Col>
			</Row> : <><b>We have been unable to get the flow data for this station in the last week.</b><br /><span style={{fontSize: "0.8em"}}>The station may be closed or down for maintenance, or we may have had issues getting the latest data.</span></>}
			{typicalFlowLowSafe && typicalFlowHighSafe ? <Row>
				<Col xs={12}><br />The normal range for flow here{station.riverName ? ', for the ' + station.riverName + ', ' : ' '}is <b>{typicalFlowLowSafe} to {typicalFlowHighSafe}</b> m3/sec.</Col>
			</Row> : null}
			{maxFlowOnRecordSafe ? <Row>
				<Col xs={12}>The maximum on record is <b>{maxFlowOnRecordSafe}</b> m3/sec. {station && station.metadata && station.metadata.maxFlowOnRecordTimestamp ? <span>Recorded at <b>{formatTimestamp(station.metadata.maxFlowOnRecordTimestamp)}</b>.</span> : null}</Col>
			</Row> : null}
			{(station && station.metadata && station.metadata.hasFlowModel ? <Col xs={12}><br />
				<span>This station has a FloodMapper flow model.</span><br />
				<CentralLink to={StationHistoricalFlowLinkCalculator(station.stationId, station.urlSlug)}><Button variant="dark" size="sm" style={{fontSize: "0.7em"}}>View Flow Model</Button></CentralLink>
			</Col> : null)}
			{(station && station.metadata && station.metadata.floodEventCount && station.metadata.floodEventCount > 0) ? <FloodEventHistoryButton
				floodEventCount={station.metadata.floodEventCount}
				earliestFloodEventTime={station.metadata.earliestFloodEventTime}
				stationId={station.stationId}
			/> : null}
		</Col>
		<Col className="d-none d-md-block" md={1} lg={2} />
		<Col xs={12}><span style={{fontSize: "0.7em"}}><i>Flow measured in Cubic Metres Per Second (m3/sec)</i></span></Col>
	</Row>,
	<Row key="flow_linechart">
		<Col xs={12}>
			{(station && !isMissingMetrics) ? <WaterLevelLineChart
				history={station.flowHistory}
				forecast={null}
				historicalMaximum={null}
				dataMode='Flow'
			/> : null}
		</Col>
	</Row>];
};


export default RenderStationFlowBased;